import type { Effort } from '../types/Effort';
import type { Task } from '../types/Task';

/** Labour row from GET /efforts/labour-by-form-ids (clone task + activities for row matching). */
export type PersistedLabourEffort = Effort & {
  task?: {
    id: number;
    taskNumber?: string | null;
    name?: string | null;
    activities?: Array<{
      id: number;
      activityNumber?: string | null;
      activityName?: string | null;
    }>;
  } | null;
};

/** Row from GET /efforts/labour-overtime-allocations-by-form-ids (per-submission DRM OT). */
export type FormLabourOvertimeAllocationRow = {
  id?: number;
  formId?: string | number;
  employeeKey?: string | null;
  originalTaskId?: number | null;
  lineKey?: string;
  sequence?: number;
  inputHours?: number | null;
  labourStraightHours?: number | null;
  labourOvertime15Hours?: number | null;
  labourOvertime20Hours?: number | null;
  classRate?: number | null;
  syncedToEffort?: boolean;
  splitSource?: string | null;
  manualAdjustedById?: number | null;
  manualAdjustedAt?: string | null;
  manualReason?: string | null;
  updatedAt?: string | null;
};

/** Must match server `LABOUR_OT_BUCKET_SUM_EPS` (overtime.labourOtBuckets). */
export const LABOUR_OT_BUCKET_SUM_EPS = 0.02;

export function isLabourOtBucketSumValid(
  straight: number,
  o15: number,
  o20: number,
  inputHours: number
): boolean {
  if (
    straight < -LABOUR_OT_BUCKET_SUM_EPS ||
    o15 < -LABOUR_OT_BUCKET_SUM_EPS ||
    o20 < -LABOUR_OT_BUCKET_SUM_EPS
  ) {
    return false;
  }
  return Math.abs(straight + o15 + o20 - inputHours) <= LABOUR_OT_BUCKET_SUM_EPS;
}

export type FormLabourMergeOptions = {
  taskName?: string;
  persistedLabourForForm?: PersistedLabourEffort[];
  /**
   * Per-submission DRM OT from FormLabourOvertimeAllocation.
   * When rows exist for a task, submission UI prefers these over Effort.labourStraightHours / OT fields
   * (effort sync can lag or skip lines that have no labour Effort row).
   */
  labourOvertimeAllocations?: FormLabourOvertimeAllocationRow[];
  /**
   * Per-line allocation key (e.g. `WorkCompleted.Tasks 1.0|task=552120`) used to narrow OT/cost
   * lookups to a single Work Completed group. Without this, multiple groups pointing to the same
   * task all read the task-level total (inflates per-row $ and OT breakdown). See server
   * `submissionLabourLines.buildOrderedSubmissionLabourLines`.
   */
  lineKey?: string;
};

/** True when any persisted DRM labour OT hour field is present (may be 0). */
export function hasLabourOtBreakdown(effort: {
  labourStraightHours?: number | null;
  labourOvertime15Hours?: number | null;
  labourOvertime20Hours?: number | null;
}): boolean {
  return (
    effort.labourStraightHours != null ||
    effort.labourOvertime15Hours != null ||
    effort.labourOvertime20Hours != null
  );
}

export function getLabourOtDisplay(effort: {
  labourStraightHours?: number | null;
  labourOvertime15Hours?: number | null;
  labourOvertime20Hours?: number | null;
}): { straight: number; ot15: number; ot20: number } {
  return {
    straight: effort.labourStraightHours ?? 0,
    ot15: effort.labourOvertime15Hours ?? 0,
    ot20: effort.labourOvertime20Hours ?? 0,
  };
}

const SUBMISSION_OT_HOURS_EPS = 1e-4;

/**
 * True when persisted DRM data shows any overtime hours for this submission
 * (allocation rows and/or synced labour efforts). Used for submissions list OT badge.
 */
export function submissionHasPersistedLabourOvertimeHours(
  allocations: FormLabourOvertimeAllocationRow[],
  efforts: PersistedLabourEffort[]
): boolean {
  for (const row of allocations) {
    const o15 = Number(row.labourOvertime15Hours ?? 0);
    const o20 = Number(row.labourOvertime20Hours ?? 0);
    if (o15 + o20 > SUBMISSION_OT_HOURS_EPS) {
      return true;
    }
  }
  for (const e of efforts) {
    if ((e.type || '').toLowerCase() !== 'labour') {
      continue;
    }
    if (!hasLabourOtBreakdown(e)) {
      continue;
    }
    const d = getLabourOtDisplay(e);
    if (d.ot15 + d.ot20 > SUBMISSION_OT_HOURS_EPS) {
      return true;
    }
  }
  return false;
}

/**
 * Billable labour line amount (matches server `labourEffortBillableAmount`).
 */
export function calculateLabourLineTotal(effort: {
  rate?: number | null;
  quantity?: number | null;
  labourStraightHours?: number | null;
  labourOvertime15Hours?: number | null;
  labourOvertime20Hours?: number | null;
}): number {
  const r = effort.rate ?? 0;
  if (!hasLabourOtBreakdown(effort)) {
    return r * (effort.quantity ?? 0);
  }
  const s = effort.labourStraightHours ?? 0;
  const o15 = effort.labourOvertime15Hours ?? 0;
  const o2 = effort.labourOvertime20Hours ?? 0;
  return r * s + r * 1.5 * o15 + r * 2 * o2;
}

/** Multi-line breakdown for a single labour effort row. */
export function formatLabourOtBreakdownText(effort: Effort): string {
  if ((effort.type || '').toLowerCase() !== 'labour') return '–';
  if (!hasLabourOtBreakdown(effort)) return '–';
  const d = getLabourOtDisplay(effort);
  return formatOtHoursLinesSkipZeros(d.straight, d.ot15, d.ot20);
}

export function formatOtHoursLines(straight: number, ot15: number, ot20: number): string {
  return `Straight: ${straight}h\nOT 1.5: ${ot15}h\nOT 2.0: ${ot20}h`;
}

/** Submission / PDF style: omit bucket lines at 0h (matches SUBMISSION_OT_HOURS_EPS). */
export function formatOtHoursLinesSkipZeros(straight: number, ot15: number, ot20: number): string {
  const lines: string[] = [];
  if (Math.abs(straight) > SUBMISSION_OT_HOURS_EPS) {
    lines.push(`Straight: ${straight}h`);
  }
  if (Math.abs(ot15) > SUBMISSION_OT_HOURS_EPS) {
    lines.push(`OT 1.5: ${ot15}h`);
  }
  if (Math.abs(ot20) > SUBMISSION_OT_HOURS_EPS) {
    lines.push(`OT 2.0: ${ot20}h`);
  }
  return lines.length > 0 ? lines.join('\n') : '–';
}

/** Parse "(#107200)" style activity number from task / activity labels. */
export function extractActivityNumberFromTaskLabel(taskName: string | undefined | null): string | null {
  if (!taskName) return null;
  const m = String(taskName).match(/\(#(\d+)\)/);
  return m ? m[1] : null;
}

function findFormLabourEffortsOnTemplateTask(
  formId: string,
  taskId: number,
  availableTasks: Task[]
): Effort[] {
  if (!taskId) return [];
  const task = availableTasks.find((t) => t.id === taskId);
  if (!task?.efforts?.length) return [];
  return task.efforts.filter(
    (e) =>
      (e.type || '').toLowerCase() === 'labour' && String(e.formId ?? '') === String(formId)
  );
}

/**
 * Map persisted labour rows (clone tasks) to one Work Completed summary row.
 */
export function matchPersistedLabourToRow(
  persisted: PersistedLabourEffort[],
  templateTaskId: number,
  taskName: string | undefined,
  availableTasks: Task[]
): Effort[] {
  if (!persisted.length) return [];

  const act = extractActivityNumberFromTaskLabel(taskName);
  if (act) {
    const byAct = persisted.filter((e) =>
      e.task?.activities?.some((a) => String(a.activityNumber ?? '') === act)
    );
    if (byAct.length === 1) return byAct;
    if (byAct.length > 1) {
      return [byAct.slice().sort((a, b) => a.id - b.id)[0]];
    }
  }

  const onTemplate = persisted.filter((e) => e.taskId === templateTaskId);
  if (onTemplate.length > 0) return onTemplate;

  const template = availableTasks.find((t) => t.id === templateTaskId);
  if (!template?.activities?.length) return [];

  const templateActIds = new Set(template.activities.map((a) => a.id));
  const related = persisted.filter((e) =>
    e.task?.activities?.some((a) => templateActIds.has(a.id))
  );

  if (related.length === 1) return related;

  if (related.length > 1 && act) {
    const narrowed = related.filter((e) =>
      e.task?.activities?.some((a) => String(a.activityNumber ?? '') === act)
    );
    if (narrowed.length === 1) return narrowed;
    if (narrowed.length > 1) {
      return [narrowed.slice().sort((a, b) => a.id - b.id)[0]];
    }
  }

  if (related.length > 1) {
    return [related.slice().sort((a, b) => a.id - b.id)[0]];
  }

  return related;
}

/**
 * Labour efforts for this form row: persisted clone-task rows first, else template task efforts.
 */
export function findFormLabourEffortsForTask(
  formId: string,
  taskId: number,
  availableTasks: Task[],
  mergeOptions?: FormLabourMergeOptions
): Effort[] {
  const persisted = mergeOptions?.persistedLabourForForm;
  if (persisted && persisted.length > 0) {
    const matched = matchPersistedLabourToRow(
      persisted,
      taskId,
      mergeOptions?.taskName,
      availableTasks
    );
    if (matched.length > 0) return matched;
  }
  return findFormLabourEffortsOnTemplateTask(formId, taskId, availableTasks);
}

/**
 * Allocation rows for a template task id (matches Work Completed `Task` / originalTaskId).
 */
function allocationsForTemplateTask(
  taskId: number,
  allocations: FormLabourOvertimeAllocationRow[] | undefined
): FormLabourOvertimeAllocationRow[] {
  if (!allocations?.length || !taskId) return [];
  return allocations.filter((a) => a.originalTaskId === taskId);
}

function allocationsForMechanicLineKey(
  lineKey: string | undefined,
  allocations: FormLabourOvertimeAllocationRow[] | undefined
): FormLabourOvertimeAllocationRow[] {
  if (!lineKey?.startsWith('Mechanic|') || !allocations?.length) return [];
  return allocations.filter((a) => a.lineKey === lineKey);
}

/**
 * Sum persisted allocation hours for a mechanic DRM line (`Mechanic|group=…`).
 */
export function sumPersistedAllocationHoursByLineKey(
  lineKey: string,
  allocations: FormLabourOvertimeAllocationRow[] | undefined
): number {
  const rows = allocationsForMechanicLineKey(lineKey, allocations);
  if (rows.length === 0) return 0;
  let t = 0;
  for (const a of rows) {
    const input = a.inputHours;
    if (input != null && !Number.isNaN(Number(input))) {
      t += Number(input);
    } else {
      t +=
        (a.labourStraightHours ?? 0) +
        (a.labourOvertime15Hours ?? 0) +
        (a.labourOvertime20Hours ?? 0);
    }
  }
  return t;
}

function allocationLineDollars(a: FormLabourOvertimeAllocationRow): number {
  const r = a.classRate ?? 0;
  const s = a.labourStraightHours ?? 0;
  const o15 = a.labourOvertime15Hours ?? 0;
  const o20 = a.labourOvertime20Hours ?? 0;
  return r * s + r * 1.5 * o15 + r * 2 * o20;
}

function aggregateAllocationsOtDisplay(allocRows: FormLabourOvertimeAllocationRow[]): string {
  let s = 0;
  let o15 = 0;
  let o20 = 0;
  for (const a of allocRows) {
    s += a.labourStraightHours ?? 0;
    o15 += a.labourOvertime15Hours ?? 0;
    o20 += a.labourOvertime20Hours ?? 0;
  }
  return formatOtHoursLinesSkipZeros(s, o15, o20);
}

/**
 * Narrow allocation rows for one Work Completed line when `lineKey` is provided.
 * Returns null when caller should treat allocations as not present for this line
 * (i.e. task has rows but none match the requested lineKey — avoid task-level inflation).
 */
function selectAllocationRowsForLine(
  taskRows: FormLabourOvertimeAllocationRow[],
  lineKey: string | undefined
): FormLabourOvertimeAllocationRow[] | null {
  if (!lineKey) return taskRows;
  const lineRows = taskRows.filter((a) => a.lineKey === lineKey);
  if (lineRows.length === 0) return null;
  return lineRows;
}

/**
 * Hours for the Work Completed labour row: sum of allocation inputHours when DRM rows exist,
 * else form-derived fallback (e.g. timecard TaskDurationHours).
 *
 * When `mergeOptions.lineKey` is set, only allocation rows for that specific line are summed.
 * If the task has rows but none match the lineKey, falls back to `fallbackHours` rather than
 * task-aggregating (prevents the same task-total from being applied to every line).
 */
export function resolveLabourInputHoursForFormTask(
  taskId: number,
  fallbackHours: number,
  mergeOptions?: FormLabourMergeOptions
): number {
  const mechanicLineRows = allocationsForMechanicLineKey(
    mergeOptions?.lineKey,
    mergeOptions?.labourOvertimeAllocations
  );
  if (mechanicLineRows.length > 0) {
    let t = 0;
    for (const a of mechanicLineRows) {
      const input = a.inputHours;
      if (input != null && !Number.isNaN(Number(input))) {
        t += Number(input);
      } else {
        t +=
          (a.labourStraightHours ?? 0) +
          (a.labourOvertime15Hours ?? 0) +
          (a.labourOvertime20Hours ?? 0);
      }
    }
    return t;
  }

  const taskRows = allocationsForTemplateTask(taskId, mergeOptions?.labourOvertimeAllocations);
  if (taskRows.length === 0) return fallbackHours;
  const rows = selectAllocationRowsForLine(taskRows, mergeOptions?.lineKey);
  if (rows == null) return fallbackHours;
  let t = 0;
  for (const a of rows) {
    const input = a.inputHours;
    if (input != null && !Number.isNaN(Number(input))) {
      t += Number(input);
    } else {
      t +=
        (a.labourStraightHours ?? 0) +
        (a.labourOvertime15Hours ?? 0) +
        (a.labourOvertime20Hours ?? 0);
    }
  }
  return t;
}

/**
 * Sum persisted FormLabourOvertimeAllocation hours for one Work Completed line (`lineKey` must match
 * server `submissionLabourLines` / summary UI). Returns 0 when no rows match that key.
 */
export function sumPersistedAllocationHoursForWorkCompleted(
  taskId: number,
  lineKey: string,
  allocations: FormLabourOvertimeAllocationRow[] | undefined
): number {
  if (!taskId || !String(lineKey).trim() || !allocations?.length) return 0;
  const taskRows = allocationsForTemplateTask(taskId, allocations);
  if (taskRows.length === 0) return 0;
  const rows = selectAllocationRowsForLine(taskRows, lineKey);
  if (rows == null) return 0;
  let t = 0;
  for (const a of rows) {
    const input = a.inputHours;
    if (input != null && !Number.isNaN(Number(input))) {
      t += Number(input);
    } else {
      t +=
        (a.labourStraightHours ?? 0) +
        (a.labourOvertime15Hours ?? 0) +
        (a.labourOvertime20Hours ?? 0);
    }
  }
  return t;
}

/**
 * Aggregated OT breakdown for submission summary: FormLabourOvertimeAllocation first, then Effort, else "—".
 *
 * When `mergeOptions.lineKey` is set, only the matching allocation row's bucket is shown.
 * If the task has rows but none match the lineKey, returns "—" (not the task-aggregated breakdown).
 */
export function aggregateFormLabourOtBreakdownDisplay(
  formId: string,
  taskId: number,
  availableTasks: Task[],
  mergeOptions?: FormLabourMergeOptions
): string {
  const mechanicLineRows = allocationsForMechanicLineKey(
    mergeOptions?.lineKey,
    mergeOptions?.labourOvertimeAllocations
  );
  if (mechanicLineRows.length > 0) {
    return aggregateAllocationsOtDisplay(mechanicLineRows);
  }

  const taskRows = allocationsForTemplateTask(taskId, mergeOptions?.labourOvertimeAllocations);
  if (taskRows.length > 0) {
    const rows = selectAllocationRowsForLine(taskRows, mergeOptions?.lineKey);
    if (rows == null) return '–';
    return aggregateAllocationsOtDisplay(rows);
  }

  const labourEfforts = findFormLabourEffortsForTask(
    formId,
    taskId,
    availableTasks,
    mergeOptions
  );
  if (labourEfforts.length) {
    let straight = 0;
    let ot15 = 0;
    let ot20 = 0;
    let anyBreakdown = false;

    for (const e of labourEfforts) {
      if (hasLabourOtBreakdown(e)) {
        anyBreakdown = true;
        const d = getLabourOtDisplay(e);
        straight += d.straight;
        ot15 += d.ot15;
        ot20 += d.ot20;
      } else {
        straight += e.quantity ?? 0;
      }
    }

    if (anyBreakdown) {
      return formatOtHoursLinesSkipZeros(straight, ot15, ot20);
    }
  }

  return '–';
}

/**
 * Labour dollar total for a task row: FormLabourOvertimeAllocation first, then Effort, else form fallback.
 *
 * When `mergeOptions.lineKey` is set, only the matching allocation row's $ is returned.
 * If the task has rows but none match the lineKey, returns `fallbackCost` (avoids charging the
 * full task total to every Work Completed group that points at the same task).
 */
export function resolveLabourLineCostForFormTask(
  formId: string,
  taskId: number,
  availableTasks: Task[],
  fallbackCost: number,
  mergeOptions?: FormLabourMergeOptions
): number {
  const mechanicLineRows = allocationsForMechanicLineKey(
    mergeOptions?.lineKey,
    mergeOptions?.labourOvertimeAllocations
  );
  if (mechanicLineRows.length > 0) {
    return mechanicLineRows.reduce((sum, a) => sum + allocationLineDollars(a), 0);
  }

  const taskRows = allocationsForTemplateTask(taskId, mergeOptions?.labourOvertimeAllocations);
  if (taskRows.length > 0) {
    const rows = selectAllocationRowsForLine(taskRows, mergeOptions?.lineKey);
    if (rows == null) return fallbackCost;
    return rows.reduce((sum, a) => sum + allocationLineDollars(a), 0);
  }

  const matches = findFormLabourEffortsForTask(formId, taskId, availableTasks, mergeOptions);
  if (matches.length > 0) {
    return matches.reduce((sum, e) => sum + calculateLabourLineTotal(e), 0);
  }

  return fallbackCost;
}

/** Numeric line cost for table footers (TaskEffortsTable): respects Other + labour OT. */
export function effortNumericLineCost(effort: Effort): number {
  const t = (effort.type || '').toLowerCase();
  if (t === 'other') return effort.quantity ?? 0;
  if (t === 'labour') return calculateLabourLineTotal(effort);
  if (effort.quantity == null || effort.rate == null) return 0;
  return effort.quantity * effort.rate;
}
